//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// ### MUI / STYLES ###
import { useTheme } from '@mui/material/styles';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import {
  Alert      ,
  Box        ,
  Snackbar   ,
  Typography ,
} from '@mui/material';

// === COMPONENTS
import { HeaderFileList } from './components/HeaderFileList/HeaderFileList';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

// === API ===
import { on, removeListener, sendMessageData } from 'api/prokey/prokey.websocket';

// === COMPONENTS ===

//#endregion

const DOCUMENT_COMPARE_METHODS = {
  num_opened: (a, b) => {
    return (a || 0) - (b || 0);
  },
  num_closed: (a, b) => {
    return (a || 0) - (b || 0);
  },
  passed: (a, b) => {
    return (a || 0) - (b || 0);
  },
  package_count: (a, b) => {
    return (a || 0) - (b || 0);
  },
  assigned_user: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
  loan_number: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
};

//-----------------------------------------------------------------
export function FileList() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme    = useTheme();
  const navigate = useNavigate();

  const [ files , setFiles ] = useState( [] );
  const [ total , setTotal ] = useState(  0 );

  // DataGrid columns definition
  const columns = [
    { field: 'upload_date', headerName: 'Upload Date', width: 170, sortable: true,
      valueFormatter: (params) => dayjs( params.value ).format( 'YYYY-MM-DD HH:mm A' )
    },
    { field: 'name'       , headerName: 'Name'       , flex :   1, sortable: true },
    { field: 'packages'   , headerName: 'Packages'   , width: 100, sortable: false },
    { field: 'documents'  , headerName: 'Documents'  , width: 100, sortable: false },
    { field: 'pages'      , headerName: 'Pages'      , width: 100, sortable: false },
    { field: 'user'       , headerName: 'Prokey User', width: 160, sortable: false },
  ];

  const [ model_page   , setModelPage   ] = useState( { page: 0, pageSize: 25 } );
  const [ model_sort   , setModelSort   ] = useState( [] );
  const [ model_filter , setModelFilter ] = useState( { items: [] } );
  
  const [ message     , setMessage     ] = useState('');
  const [ message_show, setMessageShow ] = useState( false );

  const handleModelChange_Sort   = useCallback( _handleModelChange_Sort   , [] );
  const handleModelChange_Page   = useCallback( _handleModelChange_Page   , [] );
  const handleModelChange_Filter = useCallback( _handleModelChange_Filter , [] );
  
  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      position  : 'relative',

      display : 'grid',
      gridTemplateRows : 'auto 1fr',
      gridGap : '1rem',

      padding : '1rem',

      width     : '100%',
      height    : `calc( 100% - 60px )`,
      overflow  : 'hidden',
      zIndex    : '0',

      borderRadius : '1rem',

      background : theme.palette.background.secondary[theme.palette.mode],
    },
    container2: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      padding: '0.5rem',
      overflow: 'auto',
      overflowY: 'hidden',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    title: {
      padding: '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
      gridColumn: 1 / 4,
    },
  };

  const CONTAINER = {
    style : _styles.container,
  }

  const CONTAINER2 = {
    sx: _styles.container2,
  };

  const TITLE = {
    sx: _styles.title,
    variant: 'h5',
    color: theme.palette.primary.main,
    children: ['Files: ', total],
  };

  //#endregion

  //#region [EFFECT]
  
  useEffect(() => {
    //-----------------------------------------------------------------
    function handleItems( data ) {
      //-----------------------------------------------------------------
      setFiles( data.items || [] );
      setTotal( data.count  );
      setModelPage( {  ...model_page , page: data.page } );
    }

    on( 'FILE_VIEW_INFO', handleItems );

    return () => {
      removeListener( 'FILE_VIEW_INFO' , handleItems );
    };
  }, [model_page] );
  
  useEffect(() => {

    sendMessageData( 'FILE_VIEW_INFO_SUBSCRIBING' );

    return () => {
      sendMessageData( 'FILE_VIEW_INFO_UNSUBSCRIBE' );
    };
  }, [] );
  
  useEffect(() => {
    //-----------------------------------------------------------------
    function handleSettings( data ) {
      //-----------------------------------------------------------------
      setModelFilter( { items: [] } )
      setModelSort([{ field: data.sort_header, sort: data.sort_ascend ? 'asc' : 'desc' }])

      if( [25,50,100].indexOf(data.items_per_page) < 0 ) {
        data.items_per_page = 25
      }
      setModelPage({ page: data.page, pageSize: data.items_per_page })
    }

    on( 'FILE_VIEW_INFO_SETTINGS', handleSettings )

    return () => {
      removeListener('FILE_VIEW_INFO_SETTINGS' , handleSettings );
    };
  }, [] );

  useEffect( () => {
    function handleAssigned(data) {
      const { file_id } = data;
      if( file_id == undefined ) {
        setMessage( data.message );
        setMessageShow(true);
        return;
      }
      navigate(`/files/${file_id}`);
    }

    on( 'FILE_ASSIGNED', handleAssigned );

    return () => {
      removeListener( 'FILE_ASSIGNED', handleAssigned );
    }
  }, [] );

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function _handleModelChange_Sort(model) {
    //-----------------------------------------------------------------
    setModelSort( model );
    if( model.length <= 0 ) return;
    const sort = model[0];
    sendMessageData( 'FILE_VIEW_INFO_SETTINGS', { settings: { sort_header: sort.field, sort_ascend: sort.sort === 'asc' } } )
  }
  
  //-----------------------------------------------------------------
  function _handleModelChange_Page(model) {
    //-----------------------------------------------------------------
    setModelPage( model );
    sendMessageData( 'FILE_VIEW_INFO_SETTINGS', { settings: { page: model.page, items_per_page: model.pageSize } } )
  }

  //-----------------------------------------------------------------
  function _handleModelChange_Filter(model) {
    //-----------------------------------------------------------------
    setModelFilter( model );
    sendMessageData( 'FILE_VIEW_INFO_SETTINGS', { settings: { search: model.quickFilterValues[0]||'' } } )
  }

  //-----------------------------------------------------------------
  function handleDoubleClick_File( params ) {
    //-----------------------------------------------------------------
    try {
      const file_id = params.row.file_id;
      // const file = files_filtered.find( x => (x.file_id) == (file_id) );

      // if( !file ) return;

      // if( file.assigned_user !== user_details.Username ) {
      //   setMessage('To access this file, you must be the assigned user!');
      //   setShowMessage(true);
      //   return;
      // }

      navigate(`/analytics/files/${file_id}`);
    } catch (error) {
      console.warn( error );
    }
  }

  //-----------------------------------------------------------------
  function handleMessageClose(event, reason) {
    //-----------------------------------------------------------------
    if (reason === 'clickaway') {
      return;
    }
    setMessageShow(false);
  };

  //#endregion

  //#region [METHOD]

  function getRowId(row) {
    return row.file_id;
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <>
        <HeaderFileList />
        { displayBody() }
      </>
    );
  }
  
  //-----------------------------------------------------------------
  function displayBody() {
    //-----------------------------------------------------------------
    return (
      <Box {...CONTAINER} >
        <Box {...CONTAINER2}>
          <Typography {...TITLE} />
        </Box>
        <Box sx={{ height: '100%', width: '100%', overflow: 'auto' }}>
          <DataGrid
            rows={files}
            columns={columns}
            rowCount={total}
            getRowId={getRowId}

            pageSizeOptions={[25, 50, 100]}
            paginationMode="server"
            paginationModel={model_page}
            onPaginationModelChange={handleModelChange_Page}

            sortingMode="server"
            sortModel={model_sort}
            onSortModelChange={handleModelChange_Sort}

            filterMode="server"
            filterModel={model_filter}
            onFilterModelChange={handleModelChange_Filter}

            disableRowSelectionOnClick
            onRowDoubleClick={handleDoubleClick_File}

            density="compact"
            slots={{ toolbar: CustomToolbar }}
            sx={{
              backgroundColor: theme.palette.background.primary[theme.palette.mode],
              border: 'none',
              
              '& .MuiDataGrid-row:hover': {
                backgroundColor: theme.palette.primary.main + getHexAlpha(10),
                cursor: 'pointer',
              },
            }}
          />
        </Box>
        {displayMessage()}
      </Box>
    );
  };
  
  //-----------------------------------------------------------------
  function displayMessage() {
    //-----------------------------------------------------------------
    return (
      <Snackbar open={message_show} autoHideDuration={6000} onClose={handleMessageClose}  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '&.MuiSnackbar-root': { top: '64px', right: '10px' },
        }}>
        <div>
          <Alert onClose={handleMessageClose} severity='warning' elevation={6} variant='filled' sx={{ whiteSpace: 'pre-line' }} >
            {message}
          </Alert>
        </div>
      </Snackbar>
    );
  };

  //#endregion

  return display();
}

//-----------------------------------------------------------------
function CustomToolbar() {
  //-----------------------------------------------------------------
  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GridToolbarColumnsButton />
      </Box>
      <GridToolbarQuickFilter
        debounceMs={300}
        sx={{
          minWidth: 320,
          '& .MuiInputBase-root': {
            height: 40,
            fontSize: '0.95rem',
          },
        }}
      />
    </GridToolbarContainer>
  );
}